import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Chip,
  TablePagination,
  Switch,
  FormControlLabel,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { NutzerService } from '../services/nutzerService';
import { KundenService } from '../services/kundenService';
import { Nutzer, NutzerCreateRequest, Rolle } from '../types/nutzer';
import { Kunde } from '../types/kunde';

const NutzerPage: React.FC = () => {
  const [nutzer, setNutzer] = useState<Nutzer[]>([]);
  const [kunden, setKunden] = useState<Kunde[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingNutzer, setEditingNutzer] = useState<Nutzer | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalElements, setTotalElements] = useState(0);
  const [selectedKundeId, setSelectedKundeId] = useState<number>(0);

  // Form State
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    vorname: '',
    nachname: '',
    password: '',
    rolle: Rolle.USER
  });

  useEffect(() => {
    loadNutzer();
    loadKunden();
  }, [page, rowsPerPage]);

  const loadNutzer = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await NutzerService.getAlleNutzer(page, rowsPerPage, 'id', 'asc');
      setNutzer(response.content);
      setTotalElements(response.totalElements);
    } catch (err) {
      console.error('Fehler beim Laden der Nutzer:', err);
      setError('Fehler beim Laden der Nutzer');
    } finally {
      setLoading(false);
    }
  };

  const loadKunden = async () => {
    try {
      const response = await KundenService.getAlleKunden(0, 100); // Alle Kunden für Dropdown
      setKunden(response.content);
    } catch (err) {
      console.error('Fehler beim Laden der Kunden:', err);
    }
  };

  const handleOpenDialog = (nutzer?: Nutzer) => {
    if (nutzer) {
      setEditingNutzer(nutzer);
      setFormData({
        username: nutzer.username,
        email: nutzer.email,
        vorname: nutzer.vorname,
        nachname: nutzer.nachname,
        password: '', // Password nicht vorausfüllen
        rolle: nutzer.rolle
      });
      setSelectedKundeId(nutzer.kunde?.id || 0);
    } else {
      setEditingNutzer(null);
      setFormData({
        username: '',
        email: '',
        vorname: '',
        nachname: '',
        password: '',
        rolle: Rolle.USER
      });
      setSelectedKundeId(0);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingNutzer(null);
    setFormData({
      username: '',
      email: '',
      vorname: '',
      nachname: '',
      password: '',
      rolle: Rolle.USER
    });
    setSelectedKundeId(0);
  };

  const handleSubmit = async () => {
    try {
      // Find the selected kunde object
      const selectedKunde = kunden.find(k => k.id === selectedKundeId);
      if (!selectedKunde) {
        setError('Bitte wählen Sie einen Kunden aus');
        return;
      }

      if (editingNutzer) {
        // Update existing nutzer
        await NutzerService.updateNutzer(editingNutzer.id, {
          username: formData.username,
          email: formData.email,
          vorname: formData.vorname,
          nachname: formData.nachname,
          rolle: formData.rolle,
          kunde: selectedKunde
        });
      } else {
        // Create new nutzer
        await NutzerService.createNutzer({
          ...formData,
          kunde: selectedKunde
        });
      }
      
      handleCloseDialog();
      loadNutzer();
    } catch (err) {
      console.error('Fehler beim Speichern des Nutzers:', err);
      setError('Fehler beim Speichern des Nutzers');
    }
  };

  const handleDelete = async (nutzer: Nutzer) => {
    if (window.confirm(`Möchten Sie den Nutzer "${nutzer.username}" wirklich löschen?`)) {
      try {
        await NutzerService.deleteNutzer(nutzer.id);
        loadNutzer();
      } catch (err) {
        console.error('Fehler beim Löschen des Nutzers:', err);
        setError('Fehler beim Löschen des Nutzers');
      }
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const isFormValid = () => {
    return formData.username && 
           formData.email && 
           formData.vorname && 
           formData.nachname && 
           formData.rolle &&
           selectedKundeId > 0 &&
           (!editingNutzer ? formData.password : true); // Password nur bei neuen Nutzern erforderlich
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">
          Nutzer-Verwaltung
        </Typography>
        <Box>
          <Button
            startIcon={<RefreshIcon />}
            onClick={loadNutzer}
            sx={{ mr: 1 }}
          >
            Aktualisieren
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Neuer Nutzer
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper elevation={3}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>UserName</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>E-Mail</TableCell>
                <TableCell>Rolle</TableCell>
                <TableCell>Kunde</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Erstellt am</TableCell>
                <TableCell align="center">Aktionen</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : nutzer.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    Keine Nutzer gefunden
                  </TableCell>
                </TableRow>
              ) : (
                nutzer.map((nutzer) => (
                  <TableRow key={nutzer.id} hover>
                    <TableCell>
                      <Chip label={nutzer.id} size="small" />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {nutzer.username}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {nutzer.vorname} {nutzer.nachname}
                    </TableCell>
                    <TableCell>{nutzer.email}</TableCell>
                    <TableCell>
                      <Chip 
                        label={nutzer.rolle} 
                        size="small"
                        color={nutzer.rolle === Rolle.ADMIN ? 'error' : 'primary'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={nutzer.kunde.name} 
                        size="small" 
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={nutzer.aktiv ? 'Aktiv' : 'Inaktiv'} 
                        size="small"
                        color={nutzer.aktiv ? 'success' : 'default'}
                      />
                    </TableCell>
                    <TableCell>{formatDate(nutzer.erstelltAm)}</TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog(nutzer)}
                        color="primary"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(nutzer)}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        <TablePagination
          component="div"
          count={totalElements}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Zeilen pro Seite:"
          labelDisplayedRows={({ from, to, count }) => 
            `${from}-${to} von ${count !== -1 ? count : `mehr als ${to}`}`
          }
        />
      </Paper>

      {/* Dialog für Nutzer erstellen/bearbeiten */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingNutzer ? 'Nutzer bearbeiten' : 'Neuer Nutzer'}
        </DialogTitle>
        <DialogContent>
          {/* Versteckte Felder um Browser-Autofill zu täuschen */}
          <input type="text" style={{ display: 'none' }} autoComplete="off" />
          <input type="password" style={{ display: 'none' }} autoComplete="new-password" />
          
          <TextField
            autoFocus
            margin="dense"
            label="Username"
            fullWidth
            variant="outlined"
            value={formData.username}
            onChange={(e) => setFormData({ ...formData, username: e.target.value })}
            sx={{ mb: 2 }}
            autoComplete="off"
            inputProps={{
              autoComplete: 'off',
              form: {
                autoComplete: 'off'
              }
            }}
          />
          <TextField
            margin="dense"
            label="E-Mail"
            type="email"
            fullWidth
            variant="outlined"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            sx={{ mb: 2 }}
            autoComplete="off"
            inputProps={{
              autoComplete: 'chrome-off',
            }}
          />
          <TextField
            margin="dense"
            label="Vorname"
            fullWidth
            variant="outlined"
            value={formData.vorname}
            onChange={(e) => setFormData({ ...formData, vorname: e.target.value })}
            sx={{ mb: 2 }}
            autoComplete="off"
            inputProps={{
              autoComplete: 'chrome-off',
            }}
          />
          <TextField
            margin="dense"
            label="Nachname"
            fullWidth
            variant="outlined"
            value={formData.nachname}
            onChange={(e) => setFormData({ ...formData, nachname: e.target.value })}
            sx={{ mb: 2 }}
            autoComplete="off"
            inputProps={{
              autoComplete: 'chrome-off',
            }}
          />
          {!editingNutzer && (
            <TextField
              margin="dense"
              label="Passwort"
              type="password"
              fullWidth
              variant="outlined"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              sx={{ mb: 2 }}
              autoComplete="new-password"
              inputProps={{
                autoComplete: 'new-password',
              }}
            />
          )}
          <FormControl fullWidth variant="outlined" sx={{ mb: 2 }}>
            <InputLabel>Rolle</InputLabel>
            <Select
              value={formData.rolle}
              onChange={(e) => setFormData({ ...formData, rolle: e.target.value as Rolle })}
              label="Rolle"
            >
              <MenuItem value={Rolle.USER}>User</MenuItem>
              <MenuItem value={Rolle.ADMIN}>Admin</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth variant="outlined">
            <InputLabel>Kunde</InputLabel>
            <Select
              value={selectedKundeId}
              onChange={(e) => setSelectedKundeId(e.target.value as number)}
              label="Kunde"
            >
              <MenuItem value={0}>
                <em>Bitte wählen...</em>
              </MenuItem>
              {kunden.map((kunde) => (
                <MenuItem key={kunde.id} value={kunde.id}>
                  {kunde.name} ({kunde.kundenNummer})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>
            Abbrechen
          </Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained"
            disabled={!isFormValid()}
          >
            {editingNutzer ? 'Aktualisieren' : 'Erstellen'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default NutzerPage;
